import React, { useMemo, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { Field, Form, Formik } from 'formik'
import { useToasts } from 'react-toast-notifications'
import * as Yup from 'yup'

import { CustomTextField, FormDatePicker } from 'src/form'
import _, { compact } from 'lodash'
import { DateTime } from 'luxon'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { PatientHistoryFormType } from 'src/participantHistory/constants'
import SubmitButton from './SubmitButton'
import PatientFilesUpload, { PatientFilesUploadState } from './PatientFilesUpload'

const patientFilesEnabled = process.env.PATIENT_FILES_ENABLED === 'true'

const NOTE_FORM_SCHEMA = Yup.object()
  .shape({
    date: Yup.string().default(DateTime.now().toFormat('yyyy-MM-dd')).required(),
    description: Yup.string().required(),
  })
  .required()

type NoteFormType = Yup.InferType<typeof NOTE_FORM_SCHEMA>

const NoteForm = ({ uid, formType }: { uid: string; formType: PatientHistoryFormType }) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()
  const { addToast } = useToasts()
  const [filesState, setFilesState] = useState<PatientFilesUploadState>([])
  const allFilesUploaded = useMemo(() => filesState.every((i) => i.status === 'done'), [filesState])

  return (
    <Formik<NoteFormType>
      validationSchema={NOTE_FORM_SCHEMA}
      initialValues={NOTE_FORM_SCHEMA.getDefault()}
      onSubmit={async (values, { resetForm }) => {
        const valuesWithoutDate = _.omit(values, ['date'])
        try {
          await addPatientHistoryEntry({
            uid,
            date: values.date,
            type: formType,
            value: {
              ...valuesWithoutDate,
            },
            fileIDs: compact(filesState.map((i) => (i.status === 'done' ? i.file.fileID : null))),
          })
          resetForm()
          setFilesState([])
        } catch (err) {
          const graphQLError = err instanceof ApolloError ? err.graphQLErrors[0] : undefined
          // Files that failed safety checks were deleted server-side, so their
          // fileIDs are stale. Drop them from the upload list so the next submit
          // doesn't resend them; the coach can submit the rest or re-upload to retry.
          const rejectedFileIDs = graphQLError?.extensions?.rejectedFileIDs
          if (Array.isArray(rejectedFileIDs) && rejectedFileIDs.length > 0) {
            setFilesState((prev) => prev.filter((i) => i.status !== 'done' || !rejectedFileIDs.includes(i.file.fileID)))
          }
          addToast(graphQLError?.message ?? 'Failed to save the note. Please try again.', { appearance: 'error' })
        }
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <Field component={FormDatePicker} label="Date of event" name="date" />
          <Field component={CustomTextField} label="Description" fullWidth multiline name="description" minRows={4} />
          {patientFilesEnabled && <PatientFilesUpload uid={uid} state={filesState} onChange={setFilesState} />}
          <SubmitButton isSubmitting={isSubmitting || !allFilesUploaded} />
        </Form>
      )}
    </Formik>
  )
}

export default NoteForm
